"use client";

import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { Sidebar } from "@/components/sidebar";
import { AdminGNB } from "@/components/gnb";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

interface DashboardLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
}

export function DashboardLayout({
  children,
  title,
  subtitle,
}: DashboardLayoutProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [maxWidth, setMaxWidth] = useState<string>("100%");

  useLayoutEffect(() => {
    function updateWidth() {
      if (parentRef.current) {
        setMaxWidth(parentRef.current.offsetWidth + "px");
      }
    }
    updateWidth();
    const resizeObserver = new window.ResizeObserver(updateWidth);
    if (parentRef.current) {
      resizeObserver.observe(parentRef.current);
    }
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div ref={parentRef} className="w-full">
      <div className="container mx-auto p-6" style={{ maxWidth }}>
        {children}
      </div>
    </div>
  );
}

// HOC for wrapping pages with dashboard layout
export function withDashboardLayout<T extends object>(
  Component: React.ComponentType<T>,
  pageTitle?: string,
  pageSubtitle?: string
) {
  return function WrappedComponent(props: T) {
    return (
      <DashboardLayout title={pageTitle} subtitle={pageSubtitle}>
        <Component {...props} />
      </DashboardLayout>
    );
  };
}
